import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { FiFilter, FiRefreshCw, FiUser, FiInfo } from "react-icons/fi";
import { CommonTable, Loader } from "../../Components/Common";
import CommonFilter from "../../Components/Common/CommonFilter";
import {
  GET_ALL_PAGINATION,
  RESET_PAGINATION,
  ROLES,
} from "../../Constants/Strings";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import {
  getLeaveSummary,
  refreshLeaveSummary,
} from "../../Services/LeaveServices";
import {
  LEAVE_CATEGORY_OPTIONS,
  LEAVE_SUMMARY_COLUMN_LABELS,
  LEAVE_SUMMARY_COLUMNS,
  TOAST_MESSAGES,
} from "./constants";
import { toProperCase } from "../../Utils";
import { UserContext } from "../../Context";
import { getAllUsers } from "../../Services/UserService";
import RefreshSummaryModal from "./Components/RefreshSummaryModal";

const LeaveSummaryList = () => {
  const [LeaveSummary, setLeaveSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const location = useLocation();
  const [filters, setFilters] = useState(() => {
    return (
      location.state?.filters || {
        category: "",
        user: "",
      }
    );
  });
  const [selectedUserOption, setSelectedUserOption] = useState(() => {
    return location.state?.selectedUser || null;
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [openBreakdown, setOpenBreakdown] = useState(null);

  useEffect(() => {
    fetchLeaveSummary({ offset: 0, limit: pagination.limit }, true, filters);
  }, [filters]);

  const fetchLeaveSummary = async (
    params,
    isInitial = false,
    currentFilters = filters,
  ) => {
    setLoading(true);
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([_, val]) => val !== ""),
      );

      const res = await getLeaveSummary({ ...params, ...cleanedFilters });

      if (!res || !res.data) {
        throw new Error(TOAST_MESSAGES.NO_RESPONSE_DATA);
      }

      const results = res.data.results || [];
      setLeaveSummary((prev) => (isInitial ? results : [...prev, ...results]));
      setPagination((prev) => ({
        ...prev,
        count: res.data.count || 0,
        offset: isInitial ? results.length : prev.offset + results.length,
      }));
    } catch (err) {
      toast.error(TOAST_MESSAGES.FAILED_FETCH_LEAVES);
      if (isInitial) {
        setLeaveSummary([]);
        setPagination((prev) => ({ ...prev, count: 0, offset: 0 }));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMoreLeaveSummary = () => {
    if (pagination.offset >= pagination.count) return;
    fetchLeaveSummary({ offset: pagination.offset, limit: pagination.limit });
  };

  useEffect(() => {
    const payload = { mode: "all", ...GET_ALL_PAGINATION };
    const fetchUsers = async () => {
      try {
        const res = (await getAllUsers(payload)).data.results;
        setUsers(res);
      } catch (err) {
        const msg = extractErrorMessage(err) || TOAST_MESSAGES.USER_FETCH_ERROR;
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleRefreshSummary = async ({ month, user_ids }) => {
    try {
      const selected = new Date(month + "-01");
      const today = new Date();
      const currentMonthStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        1,
      );

      if (selected < currentMonthStart) {
        toast.error(TOAST_MESSAGES.RESTRICT_PAST_MONTH_SUMMARY);
        return;
      }

      setLoading(true);

      const payload = { month };
      if (user_ids) payload.user_ids = user_ids; // optional for HR

      const response = await refreshLeaveSummary(payload);
      if (response.status === 200) {
        response.data.processed_users === 0
          ? toast.error(TOAST_MESSAGES.SUMMARY_NOT_CREATED)
          : toast.success(TOAST_MESSAGES.REFRESH_SUCCESS);
        fetchLeaveSummary(
          { offset: 0, limit: pagination.limit },
          true,
          filters,
        );
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.FAILED_TO_REFRESH;
        toast.error(msg);
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.FAILED_TO_REFRESH;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    if (
      tempFilters.user === "" &&
      tempFilters.category === "" &&
      !tempFilters.month
    ) {
      return;
    }
    setSelectedUserOption(tempFilters.user);
    tempFilters.user = tempFilters.user?.id;
    setFilters(tempFilters);
    setPagination(RESET_PAGINATION);
    setLeaveSummary([]);
    setShowFilter(false);
    // Update navigation state for back button
    navigate(".", {
      state: { filters: tempFilters, selectedUser: tempFilters.user },
      replace: true,
    });
  };
  const handleClearFilters = () => {
    const cleared = { user: "", category: "" };
    setTempFilters(cleared);
    setFilters(cleared);
    setSelectedUserOption(null);
    setPagination(RESET_PAGINATION);
    setLeaveSummary([]);

    navigate(".", { state: { filters: cleared }, replace: true });
  };

  const handleCancelFilters = () => {
    setTempFilters(filters);
    setShowFilter(false);
  };

  const handleRenderField = (key, row) => {
    if (key === "category") return toProperCase(row.category) + " Leave" || "-";
    if (key === "month") {
      if (!row.start_date) return "-";
      return new Date(row.start_date).toLocaleString("en-US", {
        month: "long",
        year: "numeric", // optional
      });
    }
    if (key === "user") return row.user_name || "-";
    if (key === "approved_leaves") return row.approved_leaves || 0;
    if (key === "earned_leaves") {
      return (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.earned_leaves || 0}</span>

          <button
            className="p-1 rounded hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              setOpenBreakdown(row);
            }}
          >
            <FiInfo className="text-gray-500 hover:text-primary" />
          </button>
        </div>
      );
    }
    if (key === "balance") return row.balance || 0;
    return row[key] || "-";
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary">Leave Summary</h2>

        <div className="flex gap-2 items-center">
          <button
            className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
            onClick={() => {
              setTempFilters({
                ...filters,
                user: selectedUserOption,
              });
              setShowFilter(true);
            }}
          >
            <FiFilter className="md:text-xl" />
          </button>
          <button
            className="flex items-center gap-2 px-3 md:py-2 py-1.5 rounded bg-primary hover:bg-green-600 text-white md:font-medium text-sm shadow-lg"
            onClick={() => setShowRefreshModal(true)}
          >
            <FiRefreshCw />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <CommonFilter
          title="Leave Summary Filter"
          tempFilters={tempFilters}
          setTempFilters={setTempFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          onCancel={handleCancelFilters}
          categoryOptions={[
            { label: "All", value: "" },
            ...LEAVE_CATEGORY_OPTIONS,
          ]}
          userOptions={
            user.role === ROLES.HR ||
            ROLES.DIRECTOR_MANAGEMENT.includes(user?.role.toLowerCase())
              ? users
              : []
          }
          useSearchableUser={
            user.role === ROLES.HR ||
            ROLES.DIRECTOR_MANAGEMENT.includes(user?.role.toLowerCase())
          }
          allowMonthFilter={true}
          selectedSearchableValue={selectedUserOption}
        />
      )}

      {/* Modal */}
      {showRefreshModal && (
        <RefreshSummaryModal
          users={users}
          currentUserRole={user.role}
          onSubmit={handleRefreshSummary} // your existing function
          onClose={() => setShowRefreshModal(false)}
        />
      )}

      {/* Loader or Data */}
      {loading && LeaveSummary.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Loader size={4} />
        </div>
      ) : LeaveSummary.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          No Leaves Available
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden md:block">
            <InfiniteScroll
              dataLength={LeaveSummary.length}
              next={loadMoreLeaveSummary}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="desktopLeaveSummary"
            >
              <div
                id="desktopLeaveSummary"
                className="h-[68vh] overflow-y-auto border rounded-md"
              >
                <CommonTable
                  tableColumns={LEAVE_SUMMARY_COLUMNS}
                  tableRows={LeaveSummary}
                  columnLabels={LEAVE_SUMMARY_COLUMN_LABELS}
                  renderField={handleRenderField}
                />
              </div>
            </InfiniteScroll>
          </div>
        </>
      )}

      {/* MOBILE VIEW (Card UI) */}
      <div className="md:hidden">
        <InfiniteScroll
          dataLength={LeaveSummary.length}
          next={loadMoreLeaveSummary}
          hasMore={pagination.offset < pagination.count}
          scrollableTarget="mobileLeaveSummary"
        >
          <div
            id="mobileLeaveSummary"
            className="h-[75vh] overflow-y-auto space-y-2 p-1"
          >
            {LeaveSummary.map((item) => {
              return (
                <div
                  key={item.id}
                  className="bg-white shadow-lg rounded-xl p-4 border border-gray-200 transition-all duration-300 hover:shadow-xl"
                >
                  {/* Header */}
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-lg text-gray-900 truncate max-w-[70%]">
                      {toProperCase(item.category)} Leave
                    </h3>
                    {/* Leave Count Badges */}
                    <div className="flex gap-2 text-xs items-center">
                      {/* Earned Leaves with Info Button */}
                      <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md whitespace-nowrap flex items-center gap-1">
                        <span>
                          Earned: <span className="font-semibold">{item.earned_leaves}</span>
                        </span>
                        {/* Info Button */}
                        <button
                          className="p-1 rounded hover:bg-gray-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenBreakdown(item);
                          }}
                        >
                          <FiInfo className="text-gray-500 hover:text-primary" size={14} />
                        </button>
                      </div>

                      <div className="bg-green-100 text-green-700 px-2 py-1 rounded-md whitespace-nowrap">
                        Approved:{" "}
                        <span className="font-semibold">
                          {item.approved_leaves}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Month Display Instead of Date Range */}
                  <div className="flex gap-1 mb-2">
                    <p className="text-sm text-gray-700 font-medium">
                      {item.start_date
                        ? new Date(item.start_date).toLocaleString("en-US", {
                            month: "long",
                            year: "numeric",
                          })
                        : "-"}
                    </p>
                  </div>

                  {/* User + Counts */}
                  <div className="w-full flex justify-between items-center border-gray-100">
                    {/* User */}
                    <div className="w-[55%] flex items-center gap-2 text-gray-700 text-sm">
                      <FiUser size={18} className="text-gray-600" />
                      <span className="font-medium truncate">
                        {item.user_name || "-"}
                      </span>
                    </div>
                    <div className="bg-red-100 text-red-700 text-sm px-2 py-1 rounded-md whitespace-nowrap">
                      Balance:{" "}
                      <span className="font-semibold">{item.balance}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </InfiniteScroll>
      </div>
      {openBreakdown && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-4 w-[300px] breakdown-popup">
            <h3 className="text-lg font-semibold mb-3">
              Earned Leave Breakdown
            </h3>

            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span>Monthly Earned</span>
                <span>{openBreakdown.monthly_earned || 0}</span>
              </div>

              <div className="flex justify-between">
                <span>Credit Leaves</span>
                <span>{openBreakdown.credit_leaves || 0}</span>
              </div>

              <div className="flex justify-between">
                <span>Debit Leaves</span>
                <span>{openBreakdown.debit_leaves || 0}</span>
              </div>

              <div className="flex justify-between font-semibold border-t pt-2">
                <span>Total Earned</span>
                <span>{openBreakdown.earned_leaves || 0}</span>
              </div>
            </div>

            <button
              className="mt-4 w-full bg-primary text-white py-1 rounded"
              onClick={() => setOpenBreakdown(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveSummaryList;
